import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import BookingGetters from "../../store/onlineBooking/getters";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxStepList, { ShareboxStep } from "./components/ShareboxStepList";
import ShareboxDetailCard from "./components/ShareboxDetailCard";
import ShareboxFooter from "./components/ShareboxFooter";

type RentalStatus = {
  rentalId?: string;
  isWebCheckInDone: boolean;
  isRaCheckoutDocSigned: boolean;
  isShareboxFlowComplete: boolean;
  errorMessage?: string;
};

type Props = {
  token: string;
  pickup: ShareboxPickupResponse;
  onNext: (doneUpTo: number) => void;
  onRentalDetailsFetched?: (movementId: string) => void;
  onRentalIdFetched?: (rentalId: string) => void;
  flowComplete?: boolean;
};

export default function D4Landing({
  token,
  pickup,
  onNext,
  onRentalDetailsFetched,
  onRentalIdFetched,
  flowComplete = false,
}: Props) {
  const { t: translate } = useTranslation();
  const { booking, reservationCode } = pickup;
  const appSettings = BookingGetters.getAppSettings();
  const contractAlreadySigned = pickup.rezIsRental === "true";
  const webCheckInAlreadyDone = pickup.isWebCheckInDone === "true";
  const isCancelled = !!pickup.isCancelled;

  const [rentalStatus, setRentalStatus] = useState<RentalStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusFetchSucceeded, setStatusFetchSucceeded] = useState(false);
  const rentalStatusFetchedRef = useRef(false);
  useEffect(() => {
    if (rentalStatusFetchedRef.current) return;
    if (flowComplete || !token || isCancelled) {
      setStatusLoading(false);
      return;
    }
    rentalStatusFetchedRef.current = true;
    BookingApi.getShareboxRentalStatus(token)
      .then((response) => {
        setStatusFetchSucceeded(true);
        const status = response.data;
        if (!status) return;
        if (status.errorMessage) {
          console.error(
            "Rental status check returned an error:",
            status.errorMessage
          );
        }
        setRentalStatus({
          rentalId: status.rentalId,
          isWebCheckInDone: !!status.isWebCheckInDone,
          isRaCheckoutDocSigned: !!status.isRaCheckoutDocSigned,
          // isRaCheckoutDocSigned: true,
          isShareboxFlowComplete: !!status.isShareboxFlowComplete,
          // isShareboxFlowComplete: false,
          errorMessage: status.errorMessage,
        });
        if (status.rentalId && onRentalIdFetched) {
          onRentalIdFetched(status.rentalId);
        }
        if (status.movementId && onRentalDetailsFetched) {
          onRentalDetailsFetched(status.movementId);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch the rental status:", error);
      })
      .finally(() => setStatusLoading(false));
  }, [token, flowComplete, onRentalIdFetched, onRentalDetailsFetched]);

  const stepDoneFlags = flowComplete
    ? [true, true, true]
    : rentalStatus && !rentalStatus.errorMessage
    ? [
        rentalStatus.isWebCheckInDone,
        rentalStatus.isRaCheckoutDocSigned,
        rentalStatus.isShareboxFlowComplete,
      ]
    : [webCheckInAlreadyDone, contractAlreadySigned, false];
  const firstNotDoneIndex = stepDoneFlags.findIndex((done) => !done);
  const doneUpTo =
    firstNotDoneIndex === -1 ? stepDoneFlags.length : firstNotDoneIndex;

  const missingRental =
    !flowComplete &&
    statusFetchSucceeded &&
    stepDoneFlags[0] &&
    !rentalStatus?.rentalId;

  const stepDefs = [
    {
      title: translate("Confirm your profile"),
      subtitle: translate("Confirm your details"),
    },
    {
      title: translate("Sign the contract"),
      subtitle: translate("OTP on your phone"),
    },
    {
      title: translate("Open the box"),
      subtitle: translate("Grab the keys and go"),
    },
  ];

  const isComplete = doneUpTo >= stepDefs.length;

  if (statusLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "background.paper",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const steps: ShareboxStep[] = stepDefs.map((step, index) => ({
    ...step,
    status: missingRental
      ? index === 0
        ? "completed"
        : "collapsed"
      : index < doneUpTo
      ? "completed"
      : index === doneUpTo
      ? "active"
      : "collapsed",
    actionLabel:
      !missingRental && index === doneUpTo ? translate("Next") : undefined,
  }));

  const ctaLabel =
    doneUpTo === 0
      ? translate("Complete your profile")
      : stepDefs[doneUpTo]?.title;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.paper" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={reservationCode}
      />

      <Box sx={{ maxWidth: 480, mx: "auto", px: 3, py: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {translate("Welcome")}, {booking.renterName}!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate("Complete the steps to pick up the car.")}
        </Typography>

        {isCancelled ? (
          <Alert severity="error">
            <AlertTitle sx={{ fontWeight: 700 }}>
              {translate("We can't complete pickup online")}
            </AlertTitle>
            {translate(
              "Online check-in isn't available because the reservation has been cancelled."
            )}
          </Alert>
        ) : (
          <ShareboxStepList
            steps={steps}
            onStepAction={() => onNext(doneUpTo)}
          />
        )}

        {!isCancelled && missingRental && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {translate("We can't complete pickup online")}
            </AlertTitle>
            {translate(
              "Your check-in has been saved, but we're unable to prepare your contract for digital signing. Please go to the rental desk at your pickup location and our staff will hand over the car."
            )}
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <ShareboxDetailCard
            title={translate("Booking details")}
            rows={[
              [translate("Reservation"), reservationCode],
              [translate("Pickup location"), booking.locationName],
              [
                translate("Start date"),
                new Date(booking.pickupAt).toLocaleString(undefined, {
                  timeZone: "UTC",
                }),
              ],
              [
                translate("End date"),
                new Date(booking.dropoffAt).toLocaleString(undefined, {
                  timeZone: "UTC",
                }),
              ],
            ]}
          />
        </Box>

        <Box sx={{ mt: 2, mb: 3 }}>
          <ShareboxDetailCard
            title={translate("Pickup details")}
            tinted
            rows={[
              [
                translate("Vehicle"),
                booking.plate
                  ? `${booking.vehicleDescription} — ${booking.plate}`
                  : booking.vehicleDescription,
              ],
              [
                translate("Cabinet"),
                `${booking.cabinet} — BOX-${booking.boxNumber}`,
              ],
            ]}
          />
        </Box>

        {(!missingRental || isCancelled) && (
          <Button
            variant="contained"
            fullWidth
            className="webCheckInButton"
            disabled={isCancelled || isComplete}
            onClick={
              isCancelled || isComplete ? undefined : () => onNext(doneUpTo)
            }
            endIcon={
              isCancelled || isComplete ? undefined : <ArrowForwardIcon />
            }
            sx={(theme) => ({
              width: "100% !important",
              maxWidth: "none !important",
              borderRadius: "12px !important",
              "&.Mui-disabled": {
                backgroundColor:
                  isComplete || isCancelled
                    ? "9C9999FF"
                    : `${theme.palette.primary.main} !important`,
                color:
                  isComplete || isCancelled ? "#000000" : "#fff !important",
              },
              color: "#fff",
            })}
          >
            {isCancelled
              ? translate("Reservation Cancelled")
              : isComplete
              ? translate("Completed")
              : ctaLabel}
          </Button>
        )}
      </Box>
      <ShareboxFooter />
    </Box>
  );
}
